import { ClipboardList, MoreHorizontal, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { mutationErrorMessage } from '@/features/common/apiError';
import { PERMISSION_ACTIONS, PROJECT_RESOURCE } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';

import { AddSubProjectDialog } from './AddSubProjectDialog';
import {
  useDetachSubProjectMutation,
  useGetReportProjectsQuery,
  useGetReportProjectSummaryQuery,
  useGetSubProjectResourcesQuery,
} from './reportsApi';
import type { ReportProjectView, SubProjectResourceRow } from './types';
import { summaryLine } from './utils';

const REMOVE_DENIED_TOOLTIP =
  "You don't have permission to remove resources. Ask an organization owner or admin.";

interface Row {
  resource: SubProjectResourceRow;
  /** `undefined` while names load; `null` once resolved and missing → "Deleted report project". */
  project: ReportProjectView | undefined | null;
}

/**
 * Sub-projects section (design §6.1) — consumed by `ProjectDetailPage` (host
 * integration reported as the P3-T9 blocker) and by `SubProjectsPage` in the
 * meantime. Rows join THREE sources on refId: the attached resource rows, the
 * caller's report-project names, and per-row summary counts. The row list (not
 * a DataTable — H1 justification §6.1) reuses Card/divider primitives only.
 */
export function SubProjectsSection({
  orgId,
  projectId,
  projectName,
}: {
  orgId: string;
  projectId: string;
  projectName: string;
}) {
  const toast = useToast();
  const resourcesQuery = useGetSubProjectResourcesQuery(projectId);
  const projectsQuery = useGetReportProjectsQuery(projectId);
  const canRemove = useCan(PERMISSION_ACTIONS.resourceRemove, PROJECT_RESOURCE);

  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    resourceId: string;
    displayName: string;
  } | null>(null);
  const [detach, detachState] = useDetachSubProjectMutation();

  const addButton = (
    <Button size="sm" onClick={() => setAddOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      Add sub-project
    </Button>
  );

  const rows: Row[] = (resourcesQuery.data ?? []).map((resource) => {
    const project = projectsQuery.data?.find((p) => p.id === resource.refId);
    return { resource, project: project ?? (projectsQuery.isLoading ? undefined : null) };
  });

  const isLoading = resourcesQuery.isLoading;
  const isError = resourcesQuery.isError || projectsQuery.isError;

  async function handleRemoveConfirm() {
    if (!removeTarget) {
      return;
    }
    try {
      await detach({ projectId, resourceId: removeTarget.resourceId }).unwrap();
      toast.success('Sub-project removed.');
      setRemoveTarget(null);
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't remove sub-project."));
    }
  }

  return (
    <section aria-labelledby="subprojects-title" className="mt-6">
      <PageHeader
        title="Sub-projects"
        titleAs="h2"
        titleId="subprojects-title"
        description="Report projects collect issue reports for this project."
        className="mb-4"
        actions={
          <Can action={PERMISSION_ACTIONS.resourceAdd} resource={PROJECT_RESOURCE}>
            {addButton}
          </Can>
        }
      />

      {isLoading ? (
        <div className="space-y-2" aria-hidden="true">
          <SkeletonBlock className="h-14 w-full" />
          <SkeletonBlock className="h-14 w-full" />
        </div>
      ) : isError ? (
        <QueryErrorView
          error={resourcesQuery.isError ? resourcesQuery.error : projectsQuery.error}
          label="sub-projects"
          onRetry={() => {
            void resourcesQuery.refetch();
            void projectsQuery.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ClipboardList />}
          title="No sub-projects"
          description="Attach a report project to collect and triage issue reports for this project."
          action={
            <Can action={PERMISSION_ACTIONS.resourceAdd} resource={PROJECT_RESOURCE}>
              {addButton}
            </Can>
          }
        />
      ) : (
        <div className="divide-y rounded-lg border bg-card">
          {rows.map(({ resource, project }) => {
            const detailPath = `/organizations/${orgId}/projects/${projectId}/reports/${resource.refId}`;
            const displayName = project?.name ?? (project === null ? 'Deleted report project' : '');

            return (
              <div
                key={resource._id}
                className="relative flex min-h-14 flex-wrap items-center gap-x-3 gap-y-1 px-3 hover:bg-accent/50"
              >
                {project ? (
                  <Link
                    to={detailPath}
                    aria-label={`Open ${displayName}`}
                    className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                ) : null}

                {project === undefined ? (
                  <SkeletonBlock className="h-4 w-40" rounded="sm" />
                ) : (
                  <span
                    className={
                      project === null
                        ? 'max-w-full truncate text-sm text-muted-foreground'
                        : 'max-w-full truncate text-sm font-medium'
                    }
                  >
                    {displayName}
                  </span>
                )}

                {project ? <SummaryCountsLine reportProjectId={project.id} /> : null}

                {project ? <span className="text-sm text-muted-foreground">Open</span> : null}

                <div className="relative z-10 ml-auto shrink-0">
                  <RowMenu
                    disabled={canRemove.allowed !== true}
                    showDeniedTooltip={canRemove.allowed === false}
                    onRemove={() => setRemoveTarget({ resourceId: resource._id, displayName })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AddSubProjectDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        projectId={projectId}
        projectName={projectName}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRemoveTarget(null);
          }
        }}
        title="Remove sub-project?"
        description={
          removeTarget
            ? `This detaches “${removeTarget.displayName}” from ${projectName}. The report project and its reports are kept.`
            : undefined
        }
        confirmLabel={detachState.isLoading ? 'Removing…' : 'Remove'}
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={detachState.isLoading}
        onConfirm={() => void handleRemoveConfirm()}
      />
    </section>
  );
}

function RowMenu({
  disabled,
  showDeniedTooltip,
  onRemove,
}: {
  disabled: boolean;
  showDeniedTooltip: boolean;
  onRemove: () => void;
}) {
  const item = (
    <DropdownMenuItem disabled={disabled} onSelect={onRemove}>
      Remove from project…
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {showDeniedTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>{item}</TooltipTrigger>
            <TooltipContent className="max-w-xs">{REMOVE_DENIED_TOOLTIP}</TooltipContent>
          </Tooltip>
        ) : (
          item
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Per-row counts with independent skeletons (A4 — parallel N+1 is the section's payload). */
function SummaryCountsLine({ reportProjectId }: { reportProjectId: string }) {
  const { data, isLoading, isError } = useGetReportProjectSummaryQuery(reportProjectId);

  if (isLoading) {
    return <SkeletonBlock className="h-4 w-40" rounded="sm" />;
  }
  // 403/404 per row: counts render "—" muted (the Open click still navigates;
  // the detail page shows the server's answer — P4).
  if (isError || !data) {
    return <span className="text-sm text-muted-foreground">—</span>;
  }
  return <span className="text-sm text-muted-foreground">{summaryLine(data)}</span>;
}
